<template>
  <!-- Feste Fenstergröße (Kundenfeedback 26.10.2026, VI): „Unternehmensvorlagen“
       und „Eigene Vorlagen“ zeigen dasselbe Fenster in der großen Ansicht –
       beim Hin- und Herwechseln springt nichts. -->
  <v-dialog
    v-model="visible"
    max-width="1080"
    persistent
    scrollable
    width="1080"
  >
    <v-card class="pick" data-testid="template-picker" rounded="lg">
      <header class="pick__head">
        <span class="pick__head-icon"><FileText :size="18" /></span>
        <div class="pick__head-text">
          <h3 class="pick__title">{{ $gt(title) }}</h3>
          <p class="pick__sub">{{ $gt(subtitle) }}</p>
        </div>
        <ButtonBase color="grey" icon-style icon-type="cancel" @click="close" />
      </header>
      <v-divider />

      <!-- Vorauswahl (Kundenfeedback 25.10.2026 Teil 2, II): erst Unternehmens-
           oder eigene Vorlagen, dann an wen die Nachricht geht. -->
      <div class="pick__filters">
        <TemplateOwnerSwitch v-model="owner" :counts="ownerCounts" />
        <div :aria-label="$gt('An wen schreiben Sie?')" class="pick__recipients" role="group">
          <span class="pick__recipients-label" :title="$gt('Es werden nur die Vorlagen für diesen Empfänger angezeigt.')">{{ $gt('An wen?') }}</span>
          <div class="kars-pills">
            <button
              :aria-pressed="category === '' ? 'true' : 'false'"
              class="kars-pill kars-pill--sm"
              data-testid="template-recipient-all"
              type="button"
              @click="category = ''"
            >{{ $gt('Alle') }}</button>
            <button
              v-for="c in categories"
              :key="c.key"
              :aria-pressed="category === c.key ? 'true' : 'false'"
              class="kars-pill kars-pill--sm"
              :data-testid="`template-recipient-${c.key}`"
              type="button"
              @click="category = c.key"
            >{{ $gt(c.label) }}</button>
          </div>
        </div>
      </div>
      <v-divider />

      <div class="pick__body">
        <!-- Liste -->
        <aside class="pick__list">
          <div class="pick__search">
            <Search :size="14" />
            <input
              ref="searchRef"
              v-model="query"
              :aria-label="$gt('Vorlage suchen…')"
              class="pick__search-input"
              data-testid="template-picker-search"
              :placeholder="$gt('Vorlage suchen…')"
              type="text"
              @keydown.down.prevent="focusItem(0)"
              @keydown.enter.prevent="selectFirst"
            >
          </div>

          <div v-if="loading" class="pick__empty">
            <v-progress-circular color="primary" indeterminate size="22" width="2" />
          </div>
          <div v-else-if="filtered.length === 0" class="pick__empty" data-testid="template-picker-empty">
            <FileText :size="24" />
            <span v-if="query">{{ $gt('Keine Vorlage passt zur Suche.') }}</span>
            <span v-else-if="category">
              {{ owner === 'own' ? $gt('Keine eigenen Vorlagen für') : $gt('Keine Unternehmensvorlagen für') }} „{{ $gt(categoryLabel(category)) }}“.
            </span>
            <span v-else>{{ owner === 'own' ? $gt('Sie haben noch keine eigenen Vorlagen.') : $gt('Noch keine Unternehmensvorlagen vorhanden.') }}</span>
            <div class="pick__empty-actions">
              <button
                v-if="otherOwnerHits > 0"
                class="pick__link"
                data-testid="template-picker-switch-owner"
                type="button"
                @click="owner = otherOwner"
              >
                {{ otherOwner === 'own' ? $gt('In eigenen Vorlagen ansehen') : $gt('In Unternehmensvorlagen ansehen') }} ({{ otherOwnerHits }})
              </button>
              <button class="pick__link" data-testid="template-picker-create" type="button" @click="manage(true)">
                <Plus :size="14" />
                {{ $gt('Neue Vorlage anlegen') }}
              </button>
            </div>
          </div>
          <div v-else ref="listRef" class="pick__groups" @keydown="onListKeydown">
            <section
              v-for="g in groups"
              :key="g.key"
              class="pick__group"
              :class="{ 'pick__group--kasko': g.key === KASKO_GROUP_KEY }"
              :data-testid="g.key === KASKO_GROUP_KEY ? 'template-picker-kasko' : undefined"
            >
              <h4 v-if="!category || g.key === KASKO_GROUP_KEY" class="pick__group-title">
                <ShieldCheck v-if="g.key === KASKO_GROUP_KEY" aria-hidden="true" :size="13" />
                {{ $gt(g.label) }}
              </h4>
              <p v-if="g.key === KASKO_GROUP_KEY" class="pick__group-hint">
                {{ $gt('Vorgefertigte Schreiben für diesen Kaskofall – an die Kaskoversicherung und an den Kunden.') }}
              </p>
              <button
                v-for="t in g.items"
                :key="t.id"
                :aria-pressed="selected?.id === t.id ? 'true' : 'false'"
                class="pick__item"
                :class="{ 'is-active': selected?.id === t.id }"
                :data-template-id="t.id"
                data-testid="template-picker-item"
                type="button"
                @click="selected = t"
                @dblclick="confirm"
              >
                <span class="pick__item-name">{{ t.name }}</span>
                <span
                  v-if="t.caseType && g.key !== KASKO_GROUP_KEY"
                  class="pick__item-case"
                  :title="$gt('Nur in Akten dieser Fallart angeboten')"
                >{{ t.caseType === 'kasko' ? $gt('Kasko') : $gt('Haftpflicht') }}</span>
              </button>
            </section>
          </div>
        </aside>

        <!-- Vorschau -->
        <section class="pick__preview">
          <template v-if="selected">
            <div class="pick__preview-field">
              <span class="pick__preview-label">{{ $gt('Betreff') }}</span>
              <span class="pick__preview-subject">{{ previewSubject || '–' }}</span>
            </div>
            <div class="pick__preview-body" v-html="previewBody" />
            <p v-if="missing.length > 0" class="pick__missing">
              <TriangleAlert :size="13" />
              <span>{{ $gt('Nicht befüllbar (bleiben im Text markiert):') }}</span>
              <code v-for="k in missing" :key="k">{{ token(k) }}</code>
            </p>
            <p v-else class="pick__ok">
              <Check :size="13" />
              {{ $gt('Alle Platzhalter können befüllt werden.') }}
            </p>
          </template>
          <div v-else class="pick__preview-empty">
            <FileText :size="26" />
            {{ $gt('Wählen Sie links eine Vorlage, um die Vorschau zu sehen.') }}
          </div>
        </section>
      </div>

      <v-divider />
      <v-card-actions class="d-flex align-center ma-2">
        <button class="pick__manage" data-testid="template-picker-manage" type="button" @click="manage(false)">
          <Settings2 :size="14" />
          {{ $gt('Vorlagen verwalten') }}
        </button>
        <v-spacer />
        <ButtonBase button-text="Abbrechen" icon-type="cancel" @click="close" />
        <ButtonBase
          button-text="Einfügen"
          :disabled="!selected"
          icon-type="check"
          variant="flat"
          @click="confirm"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
  import type { MailTemplate, MailTemplateCategory } from '@/services/mailTemplatesApi'
  import { Check, FileText, Plus, Search, Settings2, ShieldCheck, TriangleAlert } from 'lucide-vue-next'
  import { computed, nextTick, ref, watch } from 'vue'
  import ButtonBase from '@/components/buttons/ButtonBase.vue'
  import TemplateOwnerSwitch from '@/components/messages/templates/TemplateOwnerSwitch.vue'
  import {
    type MailTemplateValues,
    renderMailTemplateHtml,
    renderMailTemplateSubject,
    unresolvedPlaceholders,
  } from '@/utils/mailTemplate'
  import {
    countByOwner,
    filterByCaseType,
    filterMailTemplates,
    groupTemplatesForCase,
    initialCategory,
    KASKO_GROUP_KEY,
    readOwnerPreference,
    resolveOwner,
    type TemplateManagerLink,
    type TemplateOwner,
    writeOwnerPreference,
  } from '@/utils/mailTemplateFilter'

  /**
   * Auswahl einer E-Mail-Vorlage im Composer. Kundenfeedback Kanzlei Polz
   * 25.10.2026 Teil 2, II: oben zuerst Unternehmens- oder eigene Vorlagen
   * und den Empfänger wählen, darunter nur die passenden Vorlagen – je
   * Vorlage nur der Name (ohne Betreffzeile und Reichweiten-Plakette).
   * Rechts die Vorschau mit den echten Werten des Entwurfs (Fall, Standort,
   * Nutzer). Liefert die gewählte Vorlage über `select`.
   */
  const props = withDefaults(defineProps<{
    modelValue: boolean
    templates: MailTemplate[]
    categories: MailTemplateCategory[]
    values: MailTemplateValues
    loading?: boolean
    /** Kategorie passend zum aktuellen Empfänger der Nachricht (leer = „Alle“). */
    suggestedCategory?: string
    /**
     * Fallart der Akte (Mail RA Polz 13.10.2026): Kasko-Akten zeigen die
     * vorgefertigten Kasko-Schreiben als erste Gruppe, Vorlagen einer anderen
     * Fallart entfallen. Leer = ohne Akte, alle Vorlagen.
     */
    caseType?: string | null
    title?: string
    subtitle?: string
  }>(), {
    loading: false,
    suggestedCategory: '',
    caseType: '',
    title: 'Vorlage einfügen',
    subtitle: 'Betreff und Text werden mit den Daten des gewählten Falls, Ihres Standorts und Ihrer Benutzerdaten befüllt.',
  })

  const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void
    (e: 'select', template: MailTemplate): void
    /** „Vorlagen verwalten“ bzw. „Neue Vorlage anlegen“ mit der aktuellen Vorauswahl. */
    (e: 'manage', link: TemplateManagerLink): void
  }>()

  const visible = computed({
    get: () => props.modelValue,
    set: value => emit('update:modelValue', value),
  })

  const query = ref('')
  const selected = ref<MailTemplate | null>(null)
  const owner = ref<TemplateOwner>('shared')
  const category = ref('')
  const searchRef = ref<HTMLInputElement | null>(null)
  const listRef = ref<HTMLElement | null>(null)
  // Vorauswahl einmal je Öffnen – sobald die Vorlagen geladen sind.
  const initialized = ref(false)

  watch(() => props.modelValue, open => {
    if (!open) return
    query.value = ''
    selected.value = null
    initialized.value = false
    initFilters()
    nextTick(() => searchRef.value?.focus())
  })

  watch(() => props.loading, () => initFilters())

  function initFilters () {
    if (!props.modelValue || props.loading || initialized.value) return
    owner.value = resolveOwner(caseTemplates.value, readOwnerPreference(), props.caseType)
    category.value = initialCategory(caseTemplates.value, props.categories, owner.value, props.suggestedCategory)
    initialized.value = true
  }

  // Wahl der Doppelpille merken (gilt auch in der Vorlagenverwaltung).
  watch(owner, value => {
    if (initialized.value) writeOwnerPreference(value)
  })

  /** Vorlagen, die zur Fallart der Akte passen. */
  const caseTemplates = computed(() => filterByCaseType(props.templates, props.caseType))
  const activeTemplates = computed(() => caseTemplates.value.filter(t => t.isActive))
  const ownerCounts = computed(() => countByOwner(activeTemplates.value))

  const filtered = computed(() => filterMailTemplates(activeTemplates.value, {
    owner: owner.value,
    category: category.value,
    query: query.value,
  }))

  const groups = computed(() => groupTemplatesForCase(filtered.value, props.categories, props.caseType))

  // Auswahl verwerfen, wenn die Vorlage durch einen Filterwechsel verschwindet.
  watch(filtered, list => {
    if (selected.value && !list.some(t => t.id === selected.value?.id)) selected.value = null
  })

  const otherOwner = computed<TemplateOwner>(() => (owner.value === 'own' ? 'shared' : 'own'))
  const otherOwnerHits = computed(() => filterMailTemplates(activeTemplates.value, {
    owner: otherOwner.value,
    category: category.value,
    query: query.value,
  }).length)

  const previewSubject = computed(() => selected.value ? renderMailTemplateSubject(selected.value.subject, props.values) : '')
  const previewBody = computed(() => selected.value
    ? renderMailTemplateHtml(selected.value.bodyHtml, props.values, {
      wrapValue: v => `<span class="pick-var">${v}</span>`,
    })
    : '')
  const missing = computed(() => selected.value
    ? unresolvedPlaceholders(`${selected.value.subject} ${selected.value.bodyHtml}`, props.values)
    : [])

  /** Darstellung eines Platzhalters mit Klammern (im Template nicht direkt möglich). */
  function token (key: string): string {
    return `{{${key}}}`
  }

  function categoryLabel (key: string): string {
    return props.categories.find(c => c.key === key)?.label ?? key
  }

  // ── Tastatur: Pfeiltasten in der Liste, Enter fügt die gewählte Vorlage ein ──

  function itemButtons (): HTMLButtonElement[] {
    return [...(listRef.value?.querySelectorAll<HTMLButtonElement>('.pick__item') ?? [])]
  }

  function templateOf (el: HTMLElement | undefined): MailTemplate | null {
    const id = Number(el?.dataset.templateId)
    return filtered.value.find(t => t.id === id) ?? null
  }

  function focusItem (index: number) {
    const items = itemButtons()
    const el = items[Math.max(0, Math.min(index, items.length - 1))]
    if (!el) return
    el.focus()
    selected.value = templateOf(el)
  }

  function selectFirst () {
    if (selected.value) {
      confirm()
      return
    }
    focusItem(0)
  }

  function onListKeydown (event: KeyboardEvent) {
    const items = itemButtons()
    const index = items.indexOf(document.activeElement as HTMLButtonElement)
    if (index === -1) return
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      focusItem(index + (event.key === 'ArrowDown' ? 1 : -1))
    } else if (event.key === 'Enter' && selected.value?.id === templateOf(items[index])?.id) {
      event.preventDefault()
      confirm()
    }
  }

  function manage (create: boolean) {
    emit('manage', { owner: owner.value, category: category.value, create })
  }

  function confirm () {
    if (!selected.value) return
    emit('select', selected.value)
    visible.value = false
  }

  function close () {
    visible.value = false
  }
</script>

<style scoped lang="scss">
.pick {
  display: flex;
  flex-direction: column;
  max-height: 88vh;
}

.pick__head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 18px 22px 14px;
}

.pick__head-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, #7c3aed, #a78bfa);
  color: #fff;
  flex-shrink: 0;
}

.pick__head-text {
  flex: 1;
  min-width: 0;
}

.pick__title {
  margin: 0 0 3px;
  font-size: 1.05rem;
  font-weight: 750;
  color: var(--dark-color, #323232);
}

.pick__sub {
  margin: 0;
  font-size: 0.8rem;
  color: var(--grey-color, #5f5f5f);
}

/* Feste Höhe statt „so hoch wie der Inhalt“ (Kundenfeedback 26.10.2026, VI):
   vorher war das Fenster mit vielen Unternehmensvorlagen groß und mit wenigen
   eigenen Vorlagen klein. Jetzt immer die große Ansicht. */
.pick__body {
  display: grid;
  grid-template-columns: minmax(280px, 0.9fr) minmax(0, 1.3fr);
  height: 62vh;
  min-height: 420px;
  overflow: hidden;
}

@media (max-width: 860px) {
  .pick__body {
    grid-template-columns: 1fr;
    height: auto;
  }
}

/* ── Vorauswahl ── */
.pick__filters {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 12px 22px;
}

.pick__recipients {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
}

.pick__recipients-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--grey-color, #5f5f5f);
  cursor: help;
}

.pick__empty-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
}

.pick__link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  font: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--primary-color, #0076e5);
  cursor: pointer;

  &:hover {
    background: var(--primary-light-color, #deeefb);
  }

  &:focus-visible {
    outline: 2px solid var(--kars-focus-ring, #0076e5);
    outline-offset: 1px;
  }
}

/* ── Liste ── */
.pick__list {
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--border-grey-color-light, #e6eaf0);
  min-height: 0;
}

.pick__search {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 14px 6px;
  padding: 6px 10px;
  border: 1px solid var(--border-grey-color, #d0d5dd);
  border-radius: 10px;
  background: #fff;
  color: var(--grey-color, #5f5f5f);

  &:focus-within {
    border-color: var(--primary-color, #0076e5);
    box-shadow: 0 0 0 3px rgba(0, 118, 229, 0.12);
  }
}

.pick__search-input {
  flex: 1;
  border: none;
  outline: none;
  font: inherit;
  font-size: 0.84rem;
  background: transparent;
  color: var(--dark-color, #323232);
}

.pick__groups {
  overflow-y: auto;
  padding: 4px 8px 12px;
}

.pick__group-title {
  margin: 10px 8px 4px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--grey-color, #5f5f5f);
}

.pick__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease;

  &:hover {
    background: var(--grey-light-color, #f3f5f7);
  }

  &:focus-visible {
    outline: 2px solid var(--kars-focus-ring, #0076e5);
    outline-offset: 1px;
  }

  &.is-active {
    border-color: var(--primary-color, #0076e5);
    background: var(--primary-light-color, #deeefb);
  }
}

.pick__item-name {
  font-size: 0.86rem;
  font-weight: 650;
  color: var(--dark-color, #323232);
}

/* Fallart als kleine Plakette (Vorlagen ohne Akte, z. B. „Kasko“). */
.pick__item-case {
  padding: 1px 7px;
  border-radius: 999px;
  background: var(--kars-casetype-kasko-soft, #ede9fe);
  color: var(--kars-casetype-kasko, #5b21b6);
  font-size: 0.68rem;
  font-weight: 700;
}

/* Kasko-Schreiben (Mail RA Polz 13.10.2026): deutlich abgesetzte erste
   Gruppe in Kasko-Akten – violett wie der Kaskobereich. */
.pick__group--kasko {
  margin: 6px 0 10px;
  padding: 4px 4px 6px;
  border: 1px solid var(--kars-kasko-200, #ddd6fe);
  border-radius: 12px;
  background: var(--kars-kasko-50, #f5f3ff);

  .pick__group-title {
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--kars-casetype-kasko, #5b21b6);
  }

  .pick__item:hover {
    background: var(--kars-kasko-100, #ede9fe);
  }
}

.pick__group-hint {
  margin: 0 8px 6px;
  font-size: 0.74rem;
  line-height: 1.35;
  color: var(--grey-color, #5f5f5f);
}

.pick__item.is-active .pick__item-name {
  color: var(--primary-dark-color, #00539f);
}

.pick__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px 16px;
  font-size: 0.82rem;
  color: var(--grey-color, #5f5f5f);
  text-align: center;
}

/* ── Vorschau ── */
.pick__preview {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.pick__preview-field {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 12px 18px;
  border-bottom: 1px solid var(--border-grey-color-light, #e6eaf0);
  background: var(--grey-light-color, #f3f5f7);
}

.pick__preview-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--grey-color, #5f5f5f);
}

.pick__preview-subject {
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--dark-color, #323232);
}

.pick__preview-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 18px;
  font-size: 0.86rem;
  line-height: 1.55;

  :deep(.pick-var) {
    padding: 0 3px;
    border-radius: 4px;
    background: rgba(16, 185, 129, 0.14);
    color: #047857;
  }

  :deep(.mail-tpl-missing) {
    padding: 0 3px;
    border-radius: 4px;
    background: #fef3c7;
    color: #92400e;
    border-bottom: 2px dotted #f59e0b;
  }
}

.pick__preview-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 100%;
  min-height: 300px;
  font-size: 0.84rem;
  color: var(--grey-color, #5f5f5f);
  text-align: center;
  padding: 24px;
}

.pick__missing,
.pick__ok {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  padding: 8px 18px;
  border-top: 1px solid var(--border-grey-color-light, #e6eaf0);
  font-size: 0.76rem;

  code {
    padding: 1px 5px;
    border-radius: 4px;
    background: #fef3c7;
    color: #92400e;
  }
}

.pick__missing {
  color: #b45309;
  background: #fffbeb;
}

.pick__ok {
  color: #047857;
  background: #ecfdf5;
}

.pick__manage {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--primary-color, #0076e5);
  cursor: pointer;

  &:hover {
    background: var(--primary-light-color, #deeefb);
  }
}
</style>
